import { ReactNode, useEffect, useState } from 'react'
import Typography from '@mui/material/Typography'
import NameForm from './NameForm'
import TextFieldForm, { TextFieldFormProps } from './TextFieldForm'
import { isRealRename, renameErrorMessage, RenameSubject } from '../utilities/renameName'

export type RenameTitleProps = Pick<TextFieldFormProps, 'sx' | 'slotProps' | 'placeholder'> & {
  /** The stored name. Empty when an organization has none yet. */
  value: string
  /** What is being renamed. It selects the wording of a failure. */
  subject: RenameSubject
  /** Whether to show the field at all. The router or the database is the real gate. */
  canRename: boolean
  /** What to show when `canRename` is false. Each page owns its own typography. */
  readOnly: ReactNode
  /** Writes the new name. It must THROW on refusal — the message is read from it. */
  onRename: (name: string) => Promise<void>
  /** A DNS label, not free text: filter, lowercase and `REGEX_VALID_DOMAIN`. */
  dnsLabel?: boolean
  /** Clears a stale error when the page swaps subject without unmounting. */
  resetKey?: string
}

/**
 * The one rename control, used by the node, the network and the organization
 * (app#177). Renaming is one action; before this each page gave it a different
 * control, a different validation rule, a different permission gate and a
 * different error channel.
 *
 * What is shared: the shell (an edit-in-place field, or read-only text), the apply
 * control (the check AND the X), the no-op guard, and one inline error under the
 * field. What is NOT shared is the rule: a node name and a network name are DNS
 * labels, so those two pass `dnsLabel`; an organization name is free text and must
 * not be filtered, lowercased or matched against `REGEX_VALID_DOMAIN`.
 *
 * ⚠ Enter or the check writes; the X and a blur revert. Escape does NOT — nothing in
 * this chain handles a keydown, and MUI's TextField does not revert on it either.
 * Never `submitOnBlur` on a rename: a rename is an explicit act, and a blur is not one.
 */
export default function RenameTitle({
  value,
  subject,
  canRename,
  readOnly,
  onRename,
  dnsLabel,
  resetKey,
  ...field
}: Readonly<RenameTitleProps>) {
  const [error, setError] = useState<string>()

  // A page can swap subject without unmounting — the side menu links from one node
  // to the next — so a failure on one would otherwise sit under the next one's title
  // as if it were about that one.
  useEffect(() => setError(undefined), [resetKey])

  const submit = async (next: string) => {
    if (!isRealRename(value, next)) return
    setError(undefined)
    try {
      await onRename(next.trim())
    } catch (e) {
      setError(renameErrorMessage(e, subject))
    }
  }

  const Field = dnsLabel ? NameForm : TextFieldForm

  return (
    <>
      {canRename ? (
        <Field
          {...field}
          value={value}
          variant="standard"
          lowercase={dnsLabel}
          onSubmit={submit}
          // Typing retires a message about the name the user has just left behind.
          onChange={() => setError(undefined)}
          // ⚠⚠ THIS RENDERS THE X, AND IT MUST NOT CLEAR THE ERROR. TextFieldForm
          // calls `onCancel` from `blur()`, and `submit()` ENDS with a `blur()` — so
          // it also runs after a submit that failed, and clearing here would wipe the
          // sentence the submit had just set. Do not narrow the meaning inside
          // TextFieldForm either: NetworkInsert, TokenInsert, FileList and
          // FileBrowser all close their form from this same call after a SUCCESSFUL
          // submit, so there it means "the edit is over", not "the user pressed X".
          onCancel={() => undefined}
        />
      ) : (
        readOnly
      )}
      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </>
  )
}
